import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Activity, UserPlus, UserMinus, FilePlus, Mail, Trash2, CheckCircle2, DollarSign, MessageSquarePlus, LogIn } from "lucide-react";

const ACTION_META = {
  lead_created: { icon: <FilePlus className="w-4 h-4" />, color: "text-[#1E3A8A]", label: "New lead submitted" },
  status_changed: { icon: <Activity className="w-4 h-4" />, color: "text-amber-600", label: "Status changed" },
  marked_contacted: { icon: <CheckCircle2 className="w-4 h-4" />, color: "text-green-600", label: "Marked contacted" },
  estimate_updated: { icon: <DollarSign className="w-4 h-4" />, color: "text-green-600", label: "Estimate updated" },
  note_added: { icon: <MessageSquarePlus className="w-4 h-4" />, color: "text-[#1E3A8A]", label: "Note added" },
  note_deleted: { icon: <Trash2 className="w-4 h-4" />, color: "text-[#64748B]", label: "Note deleted" },
  lead_deleted: { icon: <Trash2 className="w-4 h-4" />, color: "text-red-600", label: "Lead deleted" },
  admin_added: { icon: <UserPlus className="w-4 h-4" />, color: "text-[#FF008F]", label: "Admin invited" },
  admin_removed: { icon: <UserMinus className="w-4 h-4" />, color: "text-red-600", label: "Admin removed" },
  admin_login: { icon: <LogIn className="w-4 h-4" />, color: "text-[#64748B]", label: "Admin signed in" },
};

export default function AdminActivity() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/activity", { params: { limit: 150 } });
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <div className="bg-white rounded-2xl border border-[#E2DDD3] p-6" data-testid="activity-view">
      <p className="text-xs uppercase tracking-wider text-[#64748B] mb-5">Recent activity</p>
      {items.length === 0 ? (
        <p className="text-sm text-[#64748B]">No activity yet.</p>
      ) : (
        <ol className="relative border-l border-[#E2DDD3] pl-6 space-y-5">
          {items.map((a) => {
            const meta = ACTION_META[a.action] || { icon: <Activity className="w-4 h-4" />, color: "text-[#64748B]", label: a.action };
            const detail = describe(a);
            return (
              <li key={a.id} className="relative" data-testid={`activity-${a.id}`}>
                <span className={`absolute -left-[31px] top-0.5 grid place-items-center w-7 h-7 rounded-full bg-[#F7F5F0] border border-[#E2DDD3] ${meta.color}`}>
                  {meta.icon}
                </span>
                <p className="text-sm text-[#0F172A]"><strong>{meta.label}</strong>{detail ? ` — ${detail}` : ""}</p>
                <p className="text-xs text-[#64748B] mt-0.5">
                  {a.actor_email ? `${a.actor_name || a.actor_email}` : "system"} · {new Date(a.created_at).toLocaleString()}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function describe(a) {
  const m = a.meta || {};
  if (a.action === "status_changed") return `${m.from || "?"} → ${m.to || "?"}`;
  if (a.action === "estimate_updated") return m.value != null ? `set to $${Number(m.value).toLocaleString()}` : "cleared";
  if (a.action === "note_added") return m.preview ? `"${m.preview}${m.preview.length >= 80 ? "…" : ""}"` : "";
  if (a.action === "lead_created") return m.source ? `via ${m.source}` : "";
  if (a.action === "admin_added" || a.action === "admin_removed") return m.email || "";
  return "";
}
